import React, {useEffect} from "react";
import {Column} from "primereact/column";
import {DataTable} from "primereact/datatable";
import {Tag} from "primereact/tag";
import {CONTENT_TYPE_UIDS} from "../../../system/content-type-registry";
import {useContentTypeData} from "../../../system/use-content-type-data";
import {useTranslate} from "../../../system/useTranslate";
import {DataState, RTL_DATATABLE_PROPS, SalesSectionHeader, StatCard, TableCard} from "../components/common";
import {entityLabel, formatDate, formatMoney, formatNumber, statusSeverity} from "../sales-utils";

export default function BillingDashboardTab() {
    const translate = useTranslate();
    const subscriptions = useContentTypeData(CONTENT_TYPE_UIDS.SUBSCRIPTION);
    const orders = useContentTypeData(CONTENT_TYPE_UIDS.ORDER);
    const invoices = useContentTypeData(CONTENT_TYPE_UIDS.INVOICE);
    const payments = useContentTypeData(CONTENT_TYPE_UIDS.PAYMENT);

    useEffect(() => {
        Promise.all([
            subscriptions.ensure({sort: "createdAt:DESC"}, {all: true}),
            orders.ensure({sort: "createdAt:DESC"}, {all: true}),
            invoices.ensure({sort: "createdAt:DESC"}, {all: true}),
            payments.ensure({sort: "createdAt:DESC"}, {all: true}),
        ]).catch(() => undefined);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const revenue = payments.items
        .filter((payment) => payment.paymentStatus === "succeeded")
        .reduce((sum, payment) => sum + Number(payment.amount || 0), 0);
    const active = subscriptions.items.filter((item) => ["trialing", "active", "past_due"].includes(item.subscriptionStatus)).length;
    const outstanding = invoices.items.filter((item) => ["issued", "overdue", "partially_paid"].includes(item.invoiceStatus)).length;
    const recent = orders.items.slice(0, 10);
    const error = subscriptions.error || orders.error || invoices.error || payments.error;
    const loading = subscriptions.loading || orders.loading || invoices.loading || payments.loading;

    return (
        <div className="flex flex-column gap-4">
            <SalesSectionHeader title={translate("billing-admin-dashboard-title")} description={translate("billing-admin-dashboard-description")}/>
            {error && <DataState error={error}/>}
            <div className="grid">
                <StatCard label={translate("billing-admin-active-subscriptions")} value={formatNumber(active)} icon="pi pi-id-card" tone="green"/>
                <StatCard label={translate("billing-admin-orders")} value={formatNumber(orders.items.length)} icon="pi pi-shopping-cart" tone="blue"/>
                <StatCard label={translate("billing-admin-outstanding-invoices")} value={formatNumber(outstanding)} icon="pi pi-file" tone="orange"/>
                <StatCard label={translate("billing-admin-settled-revenue")} value={formatMoney(revenue, "IRR", translate)} icon="pi pi-wallet" tone="green"/>
            </div>
            <TableCard>
                <DataState loading={loading} error={error} empty={!recent.length}>
                    <DataTable {...RTL_DATATABLE_PROPS} value={recent}>
                        <Column className="text-right" field="orderNumber" header={translate("sales-order-number")}/>
                        <Column className="text-right" header={translate("sales-workspace")} body={(row) => entityLabel(row.workspace)}/>
                        <Column className="text-right" header={translate("sales-amount")} body={(row) => formatMoney(row.totalAmount, row.currency, translate)}/>
                        <Column className="text-right" header={translate("sales-status")} body={(row) => <Tag className="text-xs" severity={statusSeverity(row.orderStatus)} value={row.orderStatus}/>}/>
                        <Column className="text-right" header={translate("sales-date")} body={(row) => formatDate(row.createdAt)}/>
                    </DataTable>
                </DataState>
            </TableCard>
        </div>
    );
}
